import { useCallback, useMemo } from 'react';
import {
  TaskDto,
  TaskStatus,
  extractErrorMessage,
  useSubtaskList,
  useTaskChangeLog,
  useTaskUpdate,
} from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import { NavTab } from '@sib/ui';

export enum TaskDetailsTab {
  Details = 'details',
  Subtasks = 'subtasks',
  Comments = 'comments',
  ActivityLog = 'activityLog',
}

/** Grid fields in Task Details tab (excludes title/description blocks). */
export const TASK_DETAILS_SKELETON_FIELD_COUNT = 12;

interface UseTaskDetailsTabsParams {
  task: TaskDto;
  activityLogCount: number;
}

export const useTaskDetailsTabs = ({
  task,
  activityLogCount,
}: UseTaskDetailsTabsParams): NavTab<TaskDetailsTab>[] => {
  const { t } = useTranslation();
  const { data: subtasks } = useSubtaskList(task.uuid);
  const subtasksCount = subtasks?.totalItems ?? 0;
  const commentsCount = task.commentsCount ?? 0;

  return useMemo(
    () => [
      {
        value: TaskDetailsTab.Details,
        label: t('components.taskDetails.tabs.taskDetails'),
      },
      {
        value: TaskDetailsTab.Subtasks,
        label: t('components.taskDetails.tabs.subtasks', {
          count: subtasksCount,
        }),
      },
      {
        value: TaskDetailsTab.Comments,
        label: t('components.taskDetails.tabs.comments', {
          count: commentsCount,
        }),
      },
      {
        value: TaskDetailsTab.ActivityLog,
        label: t('components.taskDetails.tabs.activityLog', {
          count: activityLogCount,
        }),
      },
    ],
    [t, subtasksCount, commentsCount, activityLogCount]
  );
};

export const useTaskActivityLogCount = (taskId: string) => {
  const { data: changelog } = useTaskChangeLog(taskId);

  return changelog?.totalItems ?? 0;
};

interface UseCompleteTaskParams {
  taskId: string;
  onCompleted?: () => void;
}

/** Marks the task as completed and runs the follow-up action once saved. */
export const useCompleteTask = ({
  taskId,
  onCompleted,
}: UseCompleteTaskParams) => {
  const { t } = useTranslation();
  const { open } = useNotification();

  const { mutate: updateTask, isPending: isCompleting } = useTaskUpdate({
    onSuccess: () => {
      open({
        type: 'success',
        message: t('notifications.edit.success', {
          entity: t('entities.task'),
        }),
      });
      onCompleted?.();
    },
    onError: (error: Error) =>
      open({
        type: 'error',
        message:
          extractErrorMessage(error) ??
          t('notifications.edit.failure', { entity: t('entities.task') }),
      }),
  });

  const completeTask = useCallback(
    () =>
      updateTask({
        uuid: taskId,
        completed: true,
        status: TaskStatus.Completed,
      }),
    [updateTask, taskId]
  );

  return { completeTask, isCompleting };
};
